import { Fragment, useEffect, useMemo, useState } from "react";
import {
  useListStockOut,
  useListInventoryItems,
  useListInventoryCategories,
  useListContacts,
  useDeleteStockOut,
  getListStockOutQueryKey,
  type ListStockOutParams,
} from "@workspace/api-client-react";
import type { StockOut, InventoryItem } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { specsToLine } from "@/lib/specs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PackageMinus, MoveRight, Gauge, IndianRupee, X, Plus, ChevronRight, ChevronDown, Trash2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { stkOutId, memId } from "@/lib/display-id";
import { StockOutForm } from "./stock-out-form";
import { RefillForm } from "./refill-form";
import { MemberStockOutForm, type MemberStockOutMode } from "./member-stock-out-form";
import { PayPerUseForm } from "./pay-per-use-form";
import { ItemCombobox } from "./item-combobox";
import { EditStockOutDialog } from "./edit-stock-out-dialog";
import { ListPagination } from "@/components/common/list-pagination";

// Phase 2.1 polish H — Stock-Out organised by charging model (matching how the
// operator thinks about consumption). 2026-05: extended from a today-only panel
// into a browsable history with date-range + item + category filters (server-side).
//   General Consumption   : Complimentary_Unlimited + Internal_Use items (coffee
//                           beans, glasses, toilet roll, cleaning). No member, no charge.
//   With Limit            : Comp_With_Limit + Plan_Inclusive_Quota items.
//   Pay Per Use           : Pay_Per_Use + AddOn_Optional items. Amount charged.
//   Internal Refills      : housekeeping bulk → floor (no member, no charge).

type Mode = "general" | "with_limit" | "pay_per_use" | "refills";

interface Props {
  openCreate?: boolean;
  setOpenCreate?: (open: boolean) => void;
}

const MODES: { id: Mode; label: string; icon: typeof PackageMinus }[] = [
  { id: "general", label: "General Consumption", icon: PackageMinus },
  { id: "with_limit", label: "With Limit", icon: Gauge },
  { id: "pay_per_use", label: "Pay Per Use", icon: IndianRupee },
  { id: "refills", label: "Internal Refills", icon: MoveRight },
];

const GENERAL_TYPES = ["Staff_Use", "Wastage", "Damaged", "Expired", "Sample_Out", "Transfer_Out"];
const WITH_LIMIT_MODELS = ["Complimentary_With_Limit", "Plan_Inclusive_Quota"];
const PAY_PER_USE_MODELS = ["Pay_Per_Use", "AddOn_Optional"];

// Each mode maps to a server-side filter group: general/refills filter by
// consumptionType; with-limit/pay-per-use filter by the item's charging model.
const MODE_PARAMS: Record<Mode, Partial<ListStockOutParams>> = {
  general: { consumptionTypes: GENERAL_TYPES.join(",") },
  refills: { consumptionTypes: "Internal_Refill" },
  with_limit: { chargingModels: WITH_LIMIT_MODELS.join(",") },
  pay_per_use: { chargingModels: PAY_PER_USE_MODELS.join(",") },
};

// "Name · Specs · (UOM)" with brand (when stored as a spec) sorted to
// the front. Matches the dropdown row in pay-per-use-form so the
// register reads identical to what the operator picked.
function fullItemLabel(it: InventoryItem): string {
  const specs = specsToLine(it.specs);
  const parts = [it.name];
  if (specs) parts.push(specs);
  parts.push(`(${it.unitOfMeasurement})`);
  return parts.join(" · ");
}

const parseLocalDate = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};
const fmtDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dayStartISO = (s: string) => { const d = parseLocalDate(s); d.setHours(0, 0, 0, 0); return d.toISOString(); };
const dayEndISO = (s: string) => { const d = parseLocalDate(s); d.setHours(23, 59, 59, 999); return d.toISOString(); };
const startOfTodayISO = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };
const endOfTodayISO = () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d.toISOString(); };

// A Pay-Per-Use "cart" — one or more stock_out rows sold together in a single
// order (they share a cart_id, stamped by the multi-line form / backfilled by
// migration 0101). Rows with no cart_id render as their own one-item cart. The
// register then shows one line per order — matching the account Transactions
// tab — instead of a separate STK-OUT row per item.
interface OpsCart {
  key: string;
  rows: StockOut[];
  minId: number;
  totalQty: number;
  totalAmount: number;
  isPaid: boolean;
  date: string;
}

function buildOpsCarts(rows: StockOut[]): OpsCart[] {
  const byCart = new Map<string, StockOut[]>();
  const solos: StockOut[] = [];
  for (const r of rows) {
    const cid = (r as unknown as { cartId?: string | null }).cartId ?? null;
    if (cid) {
      const arr = byCart.get(cid) ?? [];
      arr.push(r);
      byCart.set(cid, arr);
    } else {
      solos.push(r);
    }
  }
  const build = (rs: StockOut[], cartId: string | null): OpsCart => ({
    key: cartId ?? `solo-${rs[0]!.id}`,
    rows: rs,
    minId: Math.min(...rs.map((x) => x.id)),
    totalQty: rs.reduce((s, x) => s + Number(x.quantityIssued), 0),
    totalAmount: rs.reduce((s, x) => s + Number(x.amountCharged ?? 0), 0),
    isPaid: rs.every((x) => x.paymentStatus === "Paid"),
    date: rs.reduce(
      (min, x) => (x.transactionDate < min ? x.transactionDate : min),
      rs[0]!.transactionDate,
    ),
  });
  const carts: OpsCart[] = [];
  for (const [cid, rs] of byCart.entries()) carts.push(build(rs, cid));
  for (const r of solos) carts.push(build([r], null));
  // Newest first — mirrors the server's date-desc row order (cart siblings
  // share a timestamp, so min-id desc keeps carts in the same order).
  carts.sort((a, b) => b.minId - a.minId);
  return carts;
}

export function StockOutTab({ openCreate, setOpenCreate }: Props = {}) {
  const [mode, setMode] = useState<Mode>("general");
  const todayStr = fmtDateInput(new Date());
  const [from, setFrom] = useState(todayStr);
  const [to, setTo] = useState(todayStr);
  const [itemId, setItemId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Per-cart expand toggle for the Pay-Per-Use grouped view.
  const [expandedCarts, setExpandedCarts] = useState<Set<string>>(new Set());
  const toggleCart = (key: string) =>
    setExpandedCarts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const { data: items = [] } = useListInventoryItems({ status: "Active" });
  const { data: categories = [] } = useListInventoryCategories();
  const { data: contacts = [] } = useListContacts();

  const itemNameById = useMemo(() => {
    const m = new Map<number, string>();
    items.forEach((it) => m.set(it.id, it.name));
    return m;
  }, [items]);

  // Rich item label: "<brand?> Name · <specs> (UOM)" — same display the
  // Pay-Per-Use form uses, so the register reads the same way (two
  // "Chips" rows with different brands/packs are tellable apart).
  const itemFullById = useMemo(() => {
    const m = new Map<number, string>();
    items.forEach((it) => m.set(it.id, fullItemLabel(it)));
    return m;
  }, [items]);
  const itemFullLabel = (id: number) => itemFullById.get(id) ?? `#${id}`;

  // Look up by contact_id first (the form tags the specific person
  // who consumed the item), falling back to account_id → primary
  // contact when an older row only has the account tagged.
  const contactByContactId = useMemo(() => {
    const m = new Map<number, { name: string; account: string; accountId: number }>();
    for (const c of contacts) {
      m.set(c.id, { name: c.fullName, account: c.accountLegalName, accountId: c.accountId });
    }
    return m;
  }, [contacts]);
  const consumerByAccountId = useMemo(() => {
    const m = new Map<number, { name: string; account: string }>();
    for (const c of contacts) {
      const existing = m.get(c.accountId);
      const isPrimary = c.roles?.includes("primary");
      if (!existing || isPrimary) {
        m.set(c.accountId, { name: c.fullName, account: c.accountLegalName });
      }
    }
    return m;
  }, [contacts]);

  // Shared filters applied to every mode query.
  const filterParams: ListStockOutParams = {
    ...(from ? { dateFrom: dayStartISO(from) } : {}),
    ...(to ? { dateTo: dayEndISO(to) } : {}),
    ...(itemId != null ? { itemId } : {}),
    ...(categoryId != null ? { categoryId } : {}),
  };

  // Reset to page 1 whenever the active mode or any filter changes.
  useEffect(() => {
    setPage(1);
  }, [mode, from, to, itemId, categoryId, pageSize]);

  // One query per mode so sidebar counts stay accurate; the inactive modes are
  // fetched at pageSize 1 (we only need their total, not their rows).
  const mk = (m: Mode) => ({
    ...MODE_PARAMS[m],
    ...filterParams,
    page: mode === m ? page : 1,
    pageSize: mode === m ? pageSize : 1,
  });
  const generalQ = useListStockOut(mk("general"));
  const withLimitQ = useListStockOut(mk("with_limit"));
  const payPerUseQ = useListStockOut(mk("pay_per_use"));
  const refillsQ = useListStockOut(mk("refills"));
  const byMode = { general: generalQ, with_limit: withLimitQ, pay_per_use: payPerUseQ, refills: refillsQ };
  const activeQuery = byMode[mode];
  const rows = (activeQuery.data?.items ?? []) as StockOut[];
  const total = activeQuery.data?.total ?? 0;
  const isLoading = activeQuery.isLoading;
  const counts: Record<Mode, number> = {
    general: generalQ.data?.total ?? 0,
    with_limit: withLimitQ.data?.total ?? 0,
    pay_per_use: payPerUseQ.data?.total ?? 0,
    refills: refillsQ.data?.total ?? 0,
  };
  // Pay-Per-Use rows collapsed into carts (one line per order). Grouping is
  // page-local — a cart split across a page boundary shows partial totals on
  // each page; carts are small (a few items) and this is the register view,
  // so that edge is acceptable. The account Transactions tab groups the same
  // cart_id across all of a member's rows for billing.
  const payPerUseCarts = useMemo(() => buildOpsCarts(rows), [rows]);

  // Today strip stays scoped to today regardless of the filters above — one
  // today query, split by charging model client-side.
  const { data: todayPage } = useListStockOut({ dateFrom: startOfTodayISO(), dateTo: endOfTodayISO(), pageSize: 200 });
  const itemToModel = useMemo(() => {
    const catModel = new Map<number, string>();
    categories.forEach((c) => catModel.set(c.id, c.chargingModel));
    const m = new Map<number, string>();
    items.forEach((it) => { const mm = catModel.get(it.categoryId); if (mm) m.set(it.id, mm); });
    return m;
  }, [items, categories]);
  const today = useMemo(() => {
    const tr = todayPage?.items ?? [];
    const general = tr.filter((r) => GENERAL_TYPES.includes(r.consumptionType)).length;
    const withLimit = tr.filter((r) => {
      const m = itemToModel.get(r.itemId);
      return m === "Complimentary_With_Limit" || m === "Plan_Inclusive_Quota";
    }).length;
    const ppu = tr.filter((r) => {
      const m = itemToModel.get(r.itemId);
      return m === "Pay_Per_Use" || m === "AddOn_Optional";
    });
    const charged = ppu.reduce((s, r) => s + Number(r.amountCharged ?? 0), 0);
    return { general, withLimit, ppuCount: ppu.length, charged };
  }, [todayPage, itemToModel]);

  const anyFilter = from !== todayStr || to !== todayStr || itemId != null || categoryId != null;
  const clearFilters = () => {
    setFrom(todayStr);
    setTo(todayStr);
    setItemId(null);
    setCategoryId(null);
  };

  const sheetOpen = !!openCreate;
  function closeSheet() {
    setOpenCreate?.(false);
  }

  const itemName = (id: number) => itemNameById.get(id) ?? `#${id}`;

  // 2026-07-15 — allow the operator to undo a Pay-Per-Use sale while it's
  // still Pending. Delete cascades to the linked Pending member_charge and
  // reverses stock + entitlement on the server (operations-stock-out.ts).
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const deleteStockOut = useDeleteStockOut();
  async function onDeletePpuRow(row: StockOut) {
    const label = `STK-OUT-${String(row.id).padStart(3, "0")}`;
    const ok = window.confirm(
      `Delete ${label}? This reverses the stock movement and cancels the pending charge. This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await deleteStockOut.mutateAsync({ id: row.id });
      queryClient.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      toast({ title: `${label} deleted.` });
    } catch (err: unknown) {
      const anyErr = err as { status?: number; data?: { code?: string } };
      if (anyErr.status === 409) {
        toast({
          title: "Can't delete this row",
          description:
            anyErr.data?.code === "stock_out_charge_not_pending"
              ? "The linked charge has already been paid."
              : "The row is already settled or on a tax invoice.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Couldn't delete.", variant: "destructive" });
      }
    }
  }

  // Pull the canonical {account, contact} pair off a stock_out row.
  // Reads the post-Phase-23-08 columns directly (accountId, contactId)
  // since the generated `StockOut.memberId` is out-of-sync.
  function rowConsumer(r: StockOut): { accountId: number | null; contactId: number | null } {
    const raw = r as unknown as { accountId?: number | null; contactId?: number | null };
    return {
      accountId: raw.accountId ?? r.memberId ?? null,
      contactId: raw.contactId ?? null,
    };
  }

  // Render the Member cell — primary line is the contact full name,
  // secondary line is "<accountLegalName> · MEM-XXXX". Prefer the
  // row's tagged contactId (form-recorded specific person); fall back
  // to the account's primary contact when contactId is null (legacy
  // rows or rows where the form didn't tag a contact).
  function renderRowConsumer(r: StockOut) {
    const { accountId, contactId } = rowConsumer(r);
    if (contactId != null) {
      const c = contactByContactId.get(contactId);
      if (c) {
        return (
          <div className="flex flex-col">
            <span className="text-sm">{c.name}</span>
            <span className="text-[10px] text-muted-foreground">
              {c.account} · {memId(c.accountId)}
            </span>
          </div>
        );
      }
    }
    if (!accountId) return <span className="text-muted-foreground">—</span>;
    const c = consumerByAccountId.get(accountId);
    if (!c) return <span className="font-mono text-xs">{memId(accountId)}</span>;
    return (
      <div className="flex flex-col">
        <span className="text-sm">{c.name}</span>
        <span className="text-[10px] text-muted-foreground">
          {c.account} · {memId(accountId)}
        </span>
      </div>
    );
  }

  return (
    <>
      {/* Today's flow strip — always reflects today regardless of the filters below */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-md border bg-card px-3 py-2">
          <p className="text-xs text-muted-foreground">Today's general consumption</p>
          <p className="text-base font-semibold tabular-nums">
            {today.general} <span className="text-xs text-muted-foreground font-normal">entries</span>
          </p>
        </div>
        <div className="rounded-md border bg-card px-3 py-2">
          <p className="text-xs text-muted-foreground">Today's with-limit usage</p>
          <p className="text-base font-semibold tabular-nums">
            {today.withLimit} <span className="text-xs text-muted-foreground font-normal">issues</span>
          </p>
        </div>
        <div className="rounded-md border bg-card px-3 py-2">
          <p className="text-xs text-muted-foreground">Today's pay-per-use</p>
          <p className="text-base font-semibold tabular-nums">
            ₹{today.charged.toLocaleString("en-IN", { maximumFractionDigits: 0 })} <span className="text-xs text-muted-foreground font-normal">· {today.ppuCount} charges</span>
          </p>
        </div>
      </div>

      {/* Filter bar: date range + item + category */}
      <div className="mb-4 flex flex-wrap items-end gap-x-5 gap-y-3 rounded-md border bg-card px-3 py-2.5">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Date range</p>
          <div className="flex items-center gap-2">
            <Input type="date" aria-label="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" aria-label="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" />
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Item</p>
          <div className="flex items-center gap-1">
            <ItemCombobox value={itemId} onChange={(id) => setItemId(id)} showTier={false} placeholder="All items" className="h-9 text-sm w-[240px] font-normal" />
            {itemId != null && (
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="Clear item" onClick={() => setItemId(null)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Category</p>
          <Select
            value={categoryId != null ? String(categoryId) : "all"}
            onValueChange={(v) => setCategoryId(v === "all" ? null : Number(v))}
          >
            <SelectTrigger className="h-9 w-[200px]"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {anyFilter && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-muted-foreground">
            <X className="mr-1 h-3.5 w-3.5" />
            Reset
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-2">
          {MODES.map((m) => {
            const active = mode === m.id;
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                className={cn(
                  "w-full text-left rounded-md px-3 h-11 transition-colors flex items-center justify-between gap-3",
                  active ? "bg-primary text-primary-foreground" : "hover:bg-muted/60",
                )}
                aria-current={active ? "page" : undefined}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="text-sm font-medium truncate">{m.label}</span>
                </span>
                <span className={cn("text-xs tabular-nums", active ? "text-primary-foreground/80" : "text-muted-foreground")}>{counts[m.id]}</span>
              </button>
            );
          })}
        </aside>

        <main>
          {/* Mode-aware create CTA — sits at the top of the active
              panel (i.e. at the individual sidebar-item level), so the
              label always matches the data the user is looking at.
              Replaces the old page-level "New Stock Out" button which
              floated above the KPI strip regardless of mode and was
              misleading for Pay-Per-Use / With-Limit / Refill modes. */}
          <div className="mb-3 flex justify-end">
            <Button size="sm" onClick={() => setOpenCreate?.(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {mode === "refills"
                ? "Add Internal Refill"
                : mode === "with_limit"
                ? "Log With-Limit Usage"
                : mode === "pay_per_use"
                ? "Log Pay-Per-Use"
                : "Log Consumption"}
            </Button>
          </div>
          {mode === "general" ? (
            <ConsumptionCard
              title={`General Consumption (${total})`}
              hint="Coffee beans, glasses, stirrers, dish-wash — items consumed without member attribution."
              rows={rows}
              renderRow={(r) => (
                <>
                  <TableCell className="text-sm">{r.consumptionType.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-sm">{itemName(r.itemId)}</TableCell>
                  <TableCell className="font-mono text-sm tabular-nums">{Number(r.quantityIssued)}</TableCell>
                  <TableCell className="text-sm">{r.issuedFrom?.replace(/_/g, " ") ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{r.reason ?? ""}</TableCell>
                </>
              )}
              headers={["Reason", "Item", "Qty", "From", "Notes"]}
              emptyHint={anyFilter ? "No general consumption matches your filters." : "No general consumption logged today."}
              isLoading={isLoading}
              pagination={{ total, page, pageSize, setPage, setPageSize, noun: ["entry", "entries"] }}
            />
          ) : mode === "with_limit" ? (
            <ConsumptionCard
              title={`With-Limit Usage (${total})`}
              hint="Stationery, prints, scans — counted against a member's plan quota or with-limit allowance."
              rows={rows}
              renderRow={(r) => (
                <>
                  <TableCell>{renderRowConsumer(r)}</TableCell>
                  <TableCell className="text-sm">{r.cabinNumber ?? "—"}</TableCell>
                  <TableCell className="text-sm">{itemName(r.itemId)}</TableCell>
                  <TableCell className="font-mono text-sm tabular-nums">{Number(r.quantityIssued)}</TableCell>
                </>
              )}
              headers={["Member", "Seat / Cabin", "Item", "Qty"]}
              emptyHint={anyFilter ? "No with-limit usage matches your filters." : "No with-limit usage logged today."}
              isLoading={isLoading}
              pagination={{ total, page, pageSize, setPage, setPageSize, noun: ["issue", "issues"] }}
            />
          ) : mode === "pay_per_use" ? (
            <PayPerUseCartCard
              total={total}
              carts={payPerUseCarts}
              renderConsumer={renderRowConsumer}
              itemFullLabel={itemFullLabel}
              expanded={expandedCarts}
              onToggle={toggleCart}
              onDelete={onDeletePpuRow}
              isDeleting={deleteStockOut.isPending}
              emptyHint={anyFilter ? "No pay-per-use charges match your filters." : "No pay-per-use charges logged today."}
              isLoading={isLoading}
              pagination={{ total, page, pageSize, setPage, setPageSize, noun: ["charge", "charges"] }}
            />
          ) : (
            <ConsumptionCard
              title={`Internal Refills (${total})`}
              hint="Housekeeping moves coffee, tea bags, glasses etc. from bulk storage onto the floor."
              rows={rows}
              renderRow={(r) => (
                <>
                  <TableCell className="text-sm">{itemName(r.itemId)}</TableCell>
                  <TableCell className="font-mono text-sm tabular-nums">{Number(r.quantityIssued)}</TableCell>
                  <TableCell className="text-sm">{r.issuedFrom?.replace(/_/g, " ") ?? "—"}</TableCell>
                  <TableCell className="text-sm">{(r as { toLocation?: string | null }).toLocation?.replace(/_/g, " ") ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{r.reason ?? ""}</TableCell>
                </>
              )}
              headers={["Item", "Qty", "From", "To", "Notes"]}
              emptyHint={anyFilter ? "No internal refills match your filters." : "No internal refills logged today."}
              isLoading={isLoading}
              pagination={{ total, page, pageSize, setPage, setPageSize, noun: ["refill", "refills"] }}
            />
          )}
        </main>
      </div>

      <Sheet open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="sr-only">
            <SheetTitle>
              {mode === "refills" ? "Add Internal Refill" : mode === "with_limit" ? "Log With-Limit Usage" : mode === "pay_per_use" ? "Log Pay-Per-Use" : "New Stock Out"}
            </SheetTitle>
          </SheetHeader>
          {mode === "refills" ? (
            <RefillForm onSaved={closeSheet} onDiscard={closeSheet} />
          ) : mode === "general" ? (
            <StockOutForm onSaved={closeSheet} onDiscard={closeSheet} />
          ) : mode === "pay_per_use" ? (
            <PayPerUseForm onSaved={closeSheet} onDiscard={closeSheet} />
          ) : (
            <MemberStockOutForm
              mode={mode as MemberStockOutMode}
              onSaved={closeSheet}
              onDiscard={closeSheet}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

interface ConsumptionCardProps {
  title: string;
  hint: string;
  rows: StockOut[];
  renderRow: (r: StockOut) => React.ReactNode;
  headers: string[];
  emptyHint: string;
  isLoading: boolean;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    setPage: (p: number) => void;
    setPageSize: (n: number) => void;
    noun: [string, string];
  };
}

function ConsumptionCard({ title, hint, rows, renderRow, headers, emptyHint, isLoading, pagination }: ConsumptionCardProps) {
  // D+1 grace edit for safe non-money fields (transactionDate + reason).
  // Same behavior as PayPerUseCartCard — reuses the shared EditStockOutDialog.
  const [editingRow, setEditingRow] = useState<StockOut | null>(null);
  const isWithinEditGrace = (r: StockOut) =>
    Date.now() - new Date(r.transactionDate).getTime() < 24 * 60 * 60 * 1000;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">{hint}</p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{emptyHint}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>STK-OUT</TableHead>
                <TableHead>Date</TableHead>
                {headers.map((h) => <TableHead key={h}>{h}</TableHead>)}
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{stkOutId(r.id)}</TableCell>
                  <TableCell className="text-sm">{new Date(r.transactionDate).toLocaleDateString()}</TableCell>
                  {renderRow(r)}
                  <TableCell className="text-right">
                    {isWithinEditGrace(r) ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Edit date / note (within 24h)"
                        onClick={() => setEditingRow(r)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!isLoading && pagination.total > 0 ? (
          <ListPagination
            total={pagination.total}
            page={pagination.page}
            pageSize={pagination.pageSize}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
            isLoading={isLoading}
            noun={pagination.noun}
          />
        ) : null}
      </CardContent>
      <EditStockOutDialog
        row={editingRow}
        open={editingRow != null}
        onClose={() => setEditingRow(null)}
      />
    </Card>
  );
}

function PaymentBadge({ paid }: { paid: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
        paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800",
      )}
    >
      {paid ? "Paid" : "Pending"}
    </span>
  );
}

interface PayPerUseCartCardProps {
  total: number;
  carts: OpsCart[];
  renderConsumer: (r: StockOut) => React.ReactNode;
  itemFullLabel: (id: number) => string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  /** Delete a Pending PPU row — reverses stock/entitlement + cancels the linked charge. */
  onDelete: (row: StockOut) => void;
  isDeleting: boolean;
  emptyHint: string;
  isLoading: boolean;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    setPage: (p: number) => void;
    setPageSize: (n: number) => void;
    noun: [string, string];
  };
}

// Pay-Per-Use register grouped by cart: one line per order, with a chevron to
// expand multi-item orders into their per-item STK-OUT rows. Mirrors the
// account Transactions tab so the same sale reads the same on both screens.
function PayPerUseCartCard({
  total,
  carts,
  renderConsumer,
  itemFullLabel,
  expanded,
  onToggle,
  onDelete,
  isDeleting,
  emptyHint,
  isLoading,
  pagination,
}: PayPerUseCartCardProps) {
  // D+1 grace edit for safe non-money fields (transactionDate + reason).
  const [editingRow, setEditingRow] = useState<StockOut | null>(null);
  const isWithinEditGrace = (r: StockOut) =>
    Date.now() - new Date(r.transactionDate).getTime() < 24 * 60 * 60 * 1000;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Pay-Per-Use ({total})</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          F&amp;B, decor, add-ons — charged on consumption. Items sold together in
          one order show as a single line; expand to see each item.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>
        ) : carts.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{emptyHint}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>STK-OUT</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {carts.map((cart) => {
                const multi = cart.rows.length > 1;
                const isOpen = expanded.has(cart.key);
                const first = cart.rows[0]!;
                return (
                  <Fragment key={cart.key}>
                    <TableRow
                      className={cn(multi && "cursor-pointer")}
                      onClick={multi ? () => onToggle(cart.key) : undefined}
                    >
                      <TableCell className="font-mono text-xs">
                        <span className="inline-flex items-center gap-1">
                          {multi ? (
                            isOpen ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <span className="inline-block w-3.5" />
                          )}
                          {stkOutId(cart.minId)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{new Date(cart.date).toLocaleDateString()}</TableCell>
                      <TableCell>{renderConsumer(first)}</TableCell>
                      <TableCell className="text-sm">
                        {multi ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                              {cart.rows.length} items
                            </span>
                            <span className="text-muted-foreground truncate max-w-[220px]">
                              {itemFullLabel(first.itemId)}, …
                            </span>
                          </span>
                        ) : (
                          itemFullLabel(first.itemId)
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm tabular-nums">{cart.totalQty}</TableCell>
                      <TableCell className="font-mono text-sm tabular-nums">
                        ₹{cart.totalAmount.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell>
                        <PaymentBadge paid={cart.isPaid} />
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Edit affordance: D+1 grace, date + note only. Money-affecting
                            edits still go through DELETE + re-create. */}
                        {!multi && isWithinEditGrace(first) ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Edit date / note (within 24h)"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRow(first);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {/* Delete affordance: only on single-item carts whose one
                            row is still Pending. Multi-item carts must be
                            expanded so the operator deletes rows individually
                            (each has its own stock reversal). */}
                        {!multi && !cart.isPaid ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            title="Delete pending sale"
                            disabled={isDeleting}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(first);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {multi && isOpen
                      ? cart.rows.map((r) => {
                          const rowIsPaid = r.paymentStatus === "Paid";
                          return (
                            <TableRow key={r.id} className="bg-muted/30">
                              <TableCell className="font-mono text-[10px] text-muted-foreground pl-8">
                                {stkOutId(r.id)}
                              </TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell className="text-sm text-muted-foreground">
                                {itemFullLabel(r.itemId)}
                              </TableCell>
                              <TableCell className="font-mono text-sm tabular-nums">
                                {Number(r.quantityIssued)}
                              </TableCell>
                              <TableCell className="font-mono text-sm tabular-nums">
                                ₹{Number(r.amountCharged ?? 0).toLocaleString("en-IN")}
                              </TableCell>
                              <TableCell />
                              <TableCell className="text-right">
                                {isWithinEditGrace(r) ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    title="Edit date / note (within 24h)"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingRow(r);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                ) : null}
                                {!rowIsPaid ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-destructive"
                                    title="Delete this pending item"
                                    disabled={isDeleting}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDelete(r);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
        {!isLoading && pagination.total > 0 ? (
          <ListPagination
            total={pagination.total}
            page={pagination.page}
            pageSize={pagination.pageSize}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
            isLoading={isLoading}
            noun={pagination.noun}
          />
        ) : null}
      </CardContent>
      <EditStockOutDialog
        row={editingRow}
        open={editingRow != null}
        onClose={() => setEditingRow(null)}
      />
    </Card>
  );
}
